import React, { useMemo, useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import "./course-explorer.css";

/* =========================
   PRICING + LABELS
   ========================= */
const PRICE_SINGLE = { nano: 3400, nanocombo: 4000, ombre: 2900, lip: 3200 };
const PRICE_COMBO = {
  "nano+ombre": 5900,
  "ombre+lip": 5600,
  "nano+lip": 6100,
  "nanocombo+lip": 6700,
  "nano+ombre+lip": 8500,
};
const DEPOSIT_SINGLE = { nano: 600, nanocombo: 600, ombre: 600, lip: 600 };
const DEPOSIT_COMBO = {
  "nano+ombre": 950,
  "ombre+lip": 950,
  "nano+lip": 950,
  "nanocombo+lip": 950,
  "nano+ombre+lip": 1250,
};
const DURATIONS = { nano: 3, nanocombo: 3, ombre: 2, lip: 2 };
const LABELS   = { nano: "Full Nano Brows", nanocombo: "Nano Combo Brows", ombre: "Ombré Brows", lip: "Lip Blush" };
const ORDER    = ["nano", "nanocombo", "ombre", "lip"];
const DISPLAY_ORDER = ["ombre", "nanocombo", "nano", "lip"];
const SLUGS    = { nano: "nano", nanocombo: "nanocombo", ombre: "ombre", lip: "lips" };

/* =========================
   HELPERS
   ========================= */
const canonicalSort = (a, b) => ORDER.indexOf(a) - ORDER.indexOf(b);
const comboKey = (selection) =>
  selection?.length ? [...selection].sort(canonicalSort).join("+") : "";

function tokensFromString(str = "") {
  const s = String(str || "").toLowerCase();
  const compact = s.replace(/[\s_-]+/g, "");
  const t = [];
  if (compact.includes("nanocombo")) {
    t.push("nanocombo");
    if (compact.includes("lip")) t.push("lip");
    return t;
  }
  if (s.includes("nano")) t.push("nano");
  if (s.includes("ombr")) t.push("ombre");
  if (s.includes("lip"))  t.push("lip");
  return t;
}
function normalizeKeyFromCourseType(courseType = "") {
  const tokens = tokensFromString(courseType.replace(/\s+/g, ""));
  return comboKey(tokens);
}
function normalizeKeyFromName(name = "") {
  const tokens = tokensFromString(name);
  return comboKey(tokens);
}
function ensureId(item) {
  return (
    (item?._id && (item._id.$oid || item._id.toString?.() || item._id)) ||
    item?.appointmentType ||
    `${item?.courseType || "course"}-${item?.dates || Math.random()}`
  );
}
function sumSingles(selection) {
  return selection.reduce((s, k) => s + (PRICE_SINGLE[k] || 0), 0);
}
function comboPriceFor(selection) {
  const key = comboKey(selection);
  if (!key) return 0;
  return PRICE_COMBO[key] ?? sumSingles(selection);
}
function depositForSelection(selection) {
  const key = comboKey(selection);
  if (!key) return 0;
  return DEPOSIT_COMBO[key] ?? DEPOSIT_SINGLE[selection[0]] ?? 0;
}
function savingsFor(selection) {
  const base = sumSingles(selection);
  const combo = comboPriceFor(selection);
  return Math.max(0, base - combo);
}
function daysFor(selection) {
  return selection.reduce((s, k) => s + (DURATIONS[k] || 0), 0);
}
function incrementalPriceForAdd(k, selection) {
  const next = selection.includes(k) ? selection : [...selection, k].sort(canonicalSort);
  return Math.max(0, comboPriceFor(next) - comboPriceFor(selection));
}

// Return { monthLine: "JANUARY", dateLine: "26 - 27, 2026" }
function extractMonthLine(label = "") {
  const raw = String(label || "");
  const re = /(january|february|march|april|may|june|july|august|september|october|november|december|jan|feb|mar|apr|jun|jul|aug|sep|sept|oct|nov|dec)/i;
  const match = raw.match(re);
  if (!match) return { monthLine: "", dateLine: raw.trim() };

  const abbr = match[1].toUpperCase();
  const FULL = {
    JAN:"JANUARY", FEB:"FEBRUARY", MAR:"MARCH", APR:"APRIL",
    JUN:"JUNE", JUL:"JULY", AUG:"AUGUST", SEP:"SEPTEMBER", SEPT:"SEPTEMBER",
    OCT:"OCTOBER", NOV:"NOVEMBER", DEC:"DECEMBER"
  };
  const isFull = abbr.length > 3 || abbr === "SEPT";
  const monthLine = isFull ? abbr : (FULL[abbr] || abbr);
  const dateLine = raw.replace(re, "").replace(/\s+,/g, ",").replace(/\s+/g, " ").trim();
  return { monthLine, dateLine };
}

/* =========================
   COURSES → OPTIONS BY KEY
   ========================= */
function useOptionsByKey(courses) {
  return useMemo(() => {
    const byKey = {};
    (courses || []).forEach((raw) => {
      if (!raw || raw.sold === true) return;

      const inferred =
        normalizeKeyFromCourseType(raw.courseType) ||
        normalizeKeyFromName(raw.name);

      const key = comboKey(tokensFromString(raw.key ?? "")) || inferred;
      if (!key) return;

      const id = ensureId(raw);
      const item = { ...raw, _id: id, key };
      (byKey[key] ||= []).push(item);
    });

    Object.values(byKey).forEach((arr) =>
      arr.sort((a, b) => String(a.dates).localeCompare(String(b.dates)))
    );
    return byKey;
  }, [courses]);
}

/* =========================
   COVER IMAGE PICKER
   ========================= */
function useTechniqueCovers(courses) {
  return useMemo(() => {
    const covers = { nano: null, nanocombo: null, ombre: null, lip: null };
    const choosePoster = (c) =>
      c?.image || c?.imageURL || c?.thumbnail || (Array.isArray(c?.images) ? c.images[0] : null);

    (courses || []).forEach((c) => {
      if (!c || c.sold) return;
      const key =
        comboKey(tokensFromString(c.key ?? "")) ||
        normalizeKeyFromCourseType(c.courseType) ||
        normalizeKeyFromName(c.name);
      if (!key) return;
      if (key === "nano" && !covers.nano)   covers.nano  = choosePoster(c);
      if (key === "nanocombo" && !covers.nanocombo) covers.nanocombo = choosePoster(c);
      if (key === "ombre" && !covers.ombre) covers.ombre = choosePoster(c);
      if (key === "lip" && !covers.lip)     covers.lip   = choosePoster(c);
    });

    const CDN = "https://cosacademyassets.s3.us-east-2.amazonaws.com";
    covers.nano  = covers.nano  || `${CDN}/fnwork-sample5.webp`;
    covers.nanocombo = covers.nanocombo || `${CDN}/fncombo.webp`;
    covers.ombre = covers.ombre || `${CDN}/fnworksample1.webp`;
    covers.lip   = covers.lip   || `${CDN}/acadlips1.webp`;

    return covers;
  }, [courses]);
}

/* =========================
   ANALYTICS (stub)
   ========================= */
function track(ev, data){ try { console.log("[analytics]", ev, data); } catch {} }

/* =========================
   DATE PICKER (keyboard a11y)
   ========================= */
function DatePicker({ options = [], value, onChange }) {
  const gridRef = useRef(null);
  const [active, setActive] = useState(() => value ?? options[0]?._id ?? null);

  useEffect(() => { if (value && value !== active) setActive(value); }, [value, active]);

  function onKey(e, id, idx) {
    if (!gridRef.current) return;
    const items = [...gridRef.current.querySelectorAll('[role="option"]')];
    const move = (next) => { items[next]?.focus(); const nextId = options[next]?._id; if (nextId){ setActive(nextId); onChange?.(nextId);} };
    const last = items.length - 1;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        onChange?.(id);
        break;
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        move(Math.min(last, idx + 1));
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        move(Math.max(0, idx - 1));
        break;
      case "Home":
        e.preventDefault();
        move(0);
        break;
      case "End":
        e.preventDefault();
        move(last);
        break;
      default:
        break;
    }
  }

  return (
    <div ref={gridRef} role="listbox" aria-label="Choose a date" className="cx-date-grid">
      {options.map((o, idx) => {
        const selected = o._id === (value ?? active);
        const { monthLine, dateLine } = extractMonthLine(o.dates || "");
        return (
          <button
            key={o._id}
            type="button"
            role="option"
            aria-selected={selected}
            tabIndex={selected ? 0 : -1}
            className={`cx-date ${selected ? "is-selected" : ""}`}
            onClick={() => { onChange?.(o._id); track("date_select", { id: o._id, dates: o.dates }); }}
            onKeyDown={(e) => onKey(e, o._id, idx)}
          >
            <span className="cx-date-month">{monthLine}</span>
            <span className="cx-date-rest">{dateLine}</span>
            <span className="cx-date-loc">{o.location}</span>
          </button>
        );
      })}
      {!options.length && <div className="cx-date-empty">New dates coming soon</div>}
    </div>
  );
}

/* =========================
   URL STATE SYNC (deep links)
   ========================= */
function useUrlState(selected, setSelected, activeId, setActiveId) {
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const tech = (p.get("tech") || "").split(",").filter(Boolean);
    const date = p.get("date") || null;
    if (tech.length) setSelected(tech.sort(canonicalSort));
    if (date) setActiveId(date);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (selected.length) p.set("tech", selected.join(","));
    else p.delete("tech");
    if (activeId) p.set("date", activeId);
    else p.delete("date");
    const next = `${window.location.pathname}?${p.toString()}`;
    window.history.replaceState(null, "", next);
  }, [selected, activeId]);
}

/* =========================
   BOOKING URL BUILDER
   ========================= */
function buildBookingTarget({ options, chosenOption, activeOptionId, fallbackOptions = [] }) {
  const byId = options.find(o => o._id === activeOptionId);
  const pick = byId || chosenOption || fallbackOptions[0] || null;
  const type = pick?.appointmentType || "";
  return type
    ? `https://cosmetink.as.me/?appointmentType=${encodeURIComponent(type)}`
    : "#";
}

/* =========================
   PAGE
   ========================= */
export default function CourseExplorer({ courses = [] }) {
  const [selected, setSelected] = useState([]); // ["nano","ombre","lip"]
  const [activeOptionId, setActiveOptionId] = useState(null);
  const [stickyCollapsed, setStickyCollapsed] = useState(false);

  const byKey      = useOptionsByKey(courses);
  const covers     = useTechniqueCovers(courses);

  const comboPrice = comboPriceFor(selected);
  const deposit = depositForSelection(selected);
  const savings    = savingsFor(selected);
  const dayCount   = daysFor(selected);
  const displaySelected = DISPLAY_ORDER.filter((k) => selected.includes(k));

  const options = useMemo(() => {
    if (!selected.length) return [];
    const key = comboKey(selected);
    return (byKey[key] || []).slice();
  }, [selected, byKey]);

  const hasOneDate   = options.length === 1;
  const chosenOption = hasOneDate ? options[0] : null;

  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "smooth" }); }, []);
  useEffect(() => {
    if (hasOneDate) {
      if (activeOptionId !== options[0]._id) setActiveOptionId(options[0]._id);
    } else if (options.length === 0) {
      setActiveOptionId(null);
    }
  }, [hasOneDate, options, activeOptionId]);

  // Deep-link support
  useUrlState(selected, setSelected, activeOptionId, setActiveOptionId);

  function toggleCourse(k) {
    const blocked =
      !selected.includes(k) &&
      ((k === "nanocombo" && (selected.includes("nano") || selected.includes("ombre"))) ||
        ((k === "nano" || k === "ombre") && selected.includes("nanocombo")));
    if (blocked) return;

    setActiveOptionId(null);
    setSelected((prev) => {
      const next = prev.includes(k)
        ? prev.filter((x) => x !== k)
        : [...prev, k];
      track(prev.includes(k) ? "course_remove" : "course_add", { key: k });
      return next.sort(canonicalSort);
    });
  }

  const canBook = selected.length > 0 && options.length > 0 && ((hasOneDate && !!chosenOption) || !!activeOptionId);

  // base-tech fallback dates if combo has none
  const baseOptions = selected.length === 1 ? (byKey[selected[0] || ""] || []) : [];

  const bookingHref = buildBookingTarget({
    options,
    chosenOption,
    activeOptionId,
    fallbackOptions: baseOptions
  });

  return (
    <div className="cx-page cx-page--inline">
      {/* Hero */}
      <section className="cx-hero">
        <div className="cx-veil" />
        <div className="cx-inner">
          <h1 className="cx-title">
            Build Your <span className="cx-title-gradient">Fundamentals</span>
          </h1>
          <ul className="cx-bullets" aria-label="Key notes">
            <li><strong>Seats are limited</strong> and classes fill fast.</li>
            <li><strong>Bundle &amp; save</strong> when you combine techniques.</li>
            <li><strong>Click a card</strong> to add one or more classes.</li>
          </ul>
        </div>
      </section>

      {/* Cards — 4 across desktop, 1 per row mobile */}
      <section className="cx-composer">
        <div className="cx-grid cx-grid--covers">
          {DISPLAY_ORDER.map((k) => {
            const active = selected.includes(k);
            const disabled =
              !active &&
              ((k === "nanocombo" && (selected.includes("nano") || selected.includes("ombre"))) ||
                ((k === "nano" || k === "ombre") && selected.includes("nanocombo")));
            const cover  = covers[k];
            const isBrow = k !== "lip";
            const catClass = isBrow ? "cx-card--brow" : "cx-card--lip";
            const catLabel = isBrow ? "Brow" : "Lip";

            const addOnDelta = incrementalPriceForAdd(k, selected);
            const showAddOn  = !active && selected.length > 0;
            const priceLabel = active
              ? "Included"
              : showAddOn
                ? `Add-on for $${addOnDelta.toLocaleString()}`
                : `$${PRICE_SINGLE[k].toLocaleString()}`;
            const depositLabel = `Deposit $${DEPOSIT_SINGLE[k].toLocaleString()}`;

            return (
              <motion.button
                key={k}
                type="button"
                onClick={() => toggleCourse(k)}
                className={`cx-card cx-card--cover ${catClass} ${active ? "cx-card--active" : ""} ${disabled ? "cx-card--disabled" : ""}`}
                whileHover={disabled ? undefined : { y: -2 }}
                whileTap={disabled ? undefined : { scale: 0.98 }}
                aria-pressed={active}
                aria-disabled={disabled ? "true" : undefined}
                aria-label={`${LABELS[k]}: ${active ? "Remove" : "Add"} course`}
              >
                {/* TOP BAR ABOVE IMAGE */}
                <div className="cx-topbar">
                  <div className="cx-topbar-left">
                    <span className={`cx-cat ${isBrow ? "cx-cat--brow" : "cx-cat--lip"}`}>{catLabel}</span>
                    {active && <span className="cx-picked">Selected</span>}
                  </div>
                  <div className="cx-topbar-right">
                    <span className={`cx-check ${active ? "cx-check--on" : ""}`} aria-hidden />
                  </div>
                </div>

                {/* IMAGE */}
                <div className="cx-cover">
                  <img
                    className="cx-cover-img"
                    src={cover}
                    alt={`${LABELS[k]} class poster`}
                    loading="lazy"
                    decoding="async"
                  />
                </div>

                {/* FOOT */}
                <div className="cx-card-foot cx-card-foot--stack">
                  <div className="cx-foot-row">
                    <span className={`cx-price cx-price--badge ${showAddOn ? "cx-price--addon" : ""}`}>
                      {priceLabel}
                    </span>
                    <span className={`cx-select ${active ? "cx-select--on" : ""}`}>
                      {active ? "Added" : "Add Course"}
                    </span>
                  </div>

                  <div className="cx-div" aria-hidden="true" />

                  <div className="cx-foot-meta">
                    <Link
                      className="cx-details"
                      to={`/courses/${SLUGS[k]}`}
                      onClick={(e) => e.stopPropagation()}
                      aria-label={`View more details about ${LABELS[k]}`}
                    >
                      View more details
                    </Link>
                    <span className="cx-deposit-card">{depositLabel}</span>
                  </div>
                </div>

                {active && <span className="cx-active-glow" aria-hidden />}
              </motion.button>
            );
          })}
        </div>
      </section>

      {/* Sticky footer */}
      <div
        className={`cx-sticky ${selected.length ? "cx-sticky--show" : ""} ${stickyCollapsed ? "cx-sticky--collapsed" : ""}`}
      >
        <div className="cx-sticky-inner">

          {/* COMPACT (when collapsed) */}
          <div className="cx-sticky-compact" role="toolbar" aria-label="Compact booking bar">
            <button
              type="button"
              className="cx-collapse"
              onClick={() => setStickyCollapsed(false)}
              aria-expanded={!stickyCollapsed}
              aria-controls="cx-sticky-content"
              title="Expand details"
            >
              <span className="cx-collapse-icon" aria-hidden>▴</span>
              <span className="cx-collapse-label">Details</span>
            </button>

            <div className="cx-compact-title" aria-live="polite">
              {selected.length ? displaySelected.map((k) => LABELS[k]).join(" + ") : "Select a Course"}
            </div>

            {deposit > 0 && (
              <div className="cx-compact-deposit">
                Deposit ${deposit.toLocaleString()}
              </div>
            )}

            <a
              href={bookingHref}
              onClick={(e) => { if (!canBook) e.preventDefault(); }}
              className={`cx-cta cx-cta--nowrap cx-cta--sm ${canBook ? "" : "cx-cta--disabled"}`}
              aria-disabled={canBook ? undefined : "true"}
            >
              {canBook ? "Book" : "Select a Date"}
            </a>
          </div>

          {/* FULL (expanded) */}
          <div id="cx-sticky-content" className="cx-sticky-full">
            <div className="cx-sticky-top">
              <div className="cx-sticky-left">
                <div className="cx-sticky-title">
                  {selected.length ? displaySelected.map((k) => LABELS[k]).join(" + ") : "No course selected"}
                  {dayCount > 0 && (
                    <span className="cx-days-inline">
                      <span className="cx-days-badge" aria-label={`${dayCount} days`}>{dayCount}</span>
                      <span className="cx-days-label">Days</span>
                    </span>
                  )}
                </div>

                <div className="cx-sticky-sub">
                  {selected.length > 0 && (
                    <span className="cx-total" aria-live="polite">
                      Total&nbsp;<strong className="cx-total-num">${comboPrice.toLocaleString()}</strong>
                    </span>
                  )}
                  {deposit > 0 && (
                    <span className="cx-deposit-pill">
                      Deposit ${deposit.toLocaleString()}
                    </span>
                  )}
                  {savings > 0 && <span className="cx-save-pill">Save ${savings.toLocaleString()}</span>}
                </div>
              </div>

              <div className="cx-sticky-actions">
        <button
  type="button"
  className="cx-collapse cx-collapse--float cx-hide-desktop desktop-only"
  onClick={() => setStickyCollapsed(true)}
  aria-expanded={!stickyCollapsed}
  aria-controls="cx-sticky-content"
  title="Hide details"
>
  <span className="cx-collapse-icon" aria-hidden>▾</span>
  <span className="cx-collapse-label">Hide</span>
</button>


                <a
                  href={bookingHref}
                  onClick={(e) => { if (!canBook) e.preventDefault(); }}
                  className={`cx-cta cx-cta--nowrap ${canBook ? "" : "cx-cta--disabled"}`}
                  aria-disabled={canBook ? undefined : "true"}
                >
                  {canBook ? "Book This Course" : selected.length ? "Select a Date" : "Select a Course"}
                </a>
              </div>
            </div>

            {/* Divider between price summary and dates */}
            <div className="cx-sticky-divider" role="separator" aria-hidden="true" />

            {/* Dates: combo list or fallback to base-tech dates */}
            <div className="cx-next">
              <DatePicker
                options={options.length ? options : baseOptions}
                value={activeOptionId || chosenOption?._id || null}
                onChange={(id) => setActiveOptionId(id)}
              />
              {selected.length > 1 && !options.length && (
                <div className="cx-hint">
                  Bundle selected. New matching dates are coming soon.
                </div>
              )}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
